import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { Loader2, LogIn, Handshake } from "lucide-react";
import { useState } from "react";
import logoImage from "@/assets/logo-corre-digital.png";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import EmpresaSolicitarParceria from "./EmpresaSolicitarParceria";

export default function EmpresaLogin() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Sem usuário");

      // Check if user has admin or revisor role first (they skip partner check)
      const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", user.id).in("role", ["admin", "revisor"]);
      if (roles && roles.length > 0) {
        const isAdmin = roles.some((r) => r.role === "admin");
        navigate(isAdmin ? "/admin" : "/admin/robo-vagas", { replace: true });
        return;
      }

      // Check if user is a partner
      const { data: pu } = await supabase.from("partner_users").select("org_id").eq("user_id", user.id).maybeSingle();
      if (!pu) {
        toast({ title: "Acesso negado", description: "Sua conta não está associada a uma organização parceira.", variant: "destructive" });
        await supabase.auth.signOut();
        return;
      }

      // Navigate directly — EmpresaLayout handles terms check
      navigate("/empresa", { replace: true });
    } catch (err: any) {
      toast({ title: "Erro ao entrar", description: err.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src={logoImage} alt="Corre Digital" className="h-10 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-foreground">Acesso para Empresas e Parceiros</h1>
          <p className="text-muted-foreground text-sm mt-2">Conecte-se ao Corre Digital para gerar impacto e acessar talentos</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="login" className="flex items-center gap-1.5">
              <LogIn className="w-4 h-4" />
              Já sou parceiro
            </TabsTrigger>
            <TabsTrigger value="solicitar" className="flex items-center gap-1.5">
              <Handshake className="w-4 h-4" />
              Quero ser parceiro
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail corporativo</Label>
                <Input id="email" type="email" placeholder="seu@empresa.com" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <Button type="submit" variant="hero" size="xl" className="w-full" disabled={isLoading || !email.trim() || !password.trim()}>
                {isLoading ? (<><Loader2 className="w-5 h-5 animate-spin" />Verificando...</>) : "Entrar"}
              </Button>
            </form>
            <p className="text-center text-xs text-muted-foreground mt-4">Acesso apenas para parceiros ativos ou convidados.</p>
          </TabsContent>

          <TabsContent value="solicitar">
            <EmpresaSolicitarParceria />
          </TabsContent>
        </Tabs>

        <Button variant="link" className="w-full mt-6" onClick={() => navigate("/")}>Voltar para seleção</Button>
      </motion.div>
    </div>
  );
}
